type Op = "standard" | "full" | "alt"

/**
 * OP network label mapping for Micro SME and SME products.
 * Maps plan name (tier) + TPA + network level to the actual network label.
 */
const OP_LABELS: Record<
  string,
  Partial<Record<string, Partial<Record<Op, string>>>>
> = {
  // Micro SME plans
  "Essential Care": {
    NAS: { standard: "WN" },
    Nextcare: { standard: "RN3" },
    Mednet: { standard: "Pearl" },
  },
  "Core Care": {
    NAS: { standard: "WN", full: "SRN" },
    Nextcare: { standard: "RN3", full: "RN2" },
    Mednet: { standard: "Pearl", full: "Green" },
  },
  "Enhanced Care": {
    NAS: { standard: "WN", full: "RN" },
    Nextcare: { standard: "RN3", full: "RN" },
    Mednet: { standard: "Pearl", full: "Silver Classic" },
  },
  "Premier Care": {
    NAS: { standard: "WN", full: "GN", alt: "RN" },
    Nextcare: { standard: "RN3", full: "GN", alt: "RN" },
    Mednet: {
      standard: "Pearl",
      full: "Silver Premium",
      alt: "Silver Classic",
    },
  },
  "Elite Care": {
    NAS: { standard: "WN", full: "CN" },
    Nextcare: { standard: "RN3", full: "GN+" },
    Mednet: { standard: "Pearl", full: "Gold" },
  },
  // SME plans
  "CORE": {
    NAS: { standard: "WN" },
    Nextcare: { standard: "RN3" },
    Mednet: { standard: "SilkRoad" },
  },
  "Advanced": {
    NAS: { standard: "SRN" },
    Nextcare: { standard: "RN2" },
    Mednet: { standard: "Green" },
  },
  "Premium": {
    NAS: { standard: "RN" },
    Nextcare: { standard: "RN" },
    Mednet: { standard: "SilverClassic" },
  },
  "Premium Plus": {
    NAS: { standard: "GN" },
    Nextcare: { standard: "GN" },
    Mednet: { standard: "SilverPremium" },
  },
  "Superior": {
    NAS: { standard: "CN" },
    Nextcare: { standard: "GN+" },
    Mednet: { standard: "Gold" },
  },
}

/**
 * IP network label mapping for Micro SME and SME products.
 * Maps plan name (tier) + TPA to the inpatient network label.
 */
const IP_LABELS: Record<
  string,
  Partial<Record<string, string>>
> = {
  // Micro SME plans
  "Essential Care": {
    NAS: "WN",
    Nextcare: "RN3",
    Mednet: "Pearl",
  },
  "Core Care": {
    NAS: "SRN",
    Nextcare: "RN2",
    Mednet: "Green",
  },
  "Enhanced Care": {
    NAS: "RN",
    Nextcare: "RN",
    Mednet: "Silver Classic",
  },
  "Premier Care": {
    NAS: "GN",
    Nextcare: "GN+",
    Mednet: "Silver Premium",
  },
  "Elite Care": {
    NAS: "CN",
    Nextcare: "GN+",
    Mednet: "Gold",
  },
  // SME plans
  "CORE": {
    NAS: "WN",
    Nextcare: "RN3",
    Mednet: "SilkRoad",
  },
  "Advanced": {
    NAS: "SRN",
    Nextcare: "RN2",
    Mednet: "Green",
  },
  "Premium": {
    NAS: "RN",
    Nextcare: "RN",
    Mednet: "SilverClassic",
  },
  "Premium Plus": {
    NAS: "GN",
    Nextcare: "GN",
    Mednet: "SilverPremium",
  },
  "Superior": {
    NAS: "CN",
    Nextcare: "GN+",
    Mednet: "Gold",
  },
}

/**
 * Get the OP network label for a Micro SME/SME plan based on plan name, TPA, and network level.
 * Returns undefined if the combination is not found.
 */
export function getOpNetworkLabel(
  planName: string | undefined,
  tpa: string | undefined,
  networkLevel: Op,
): string | undefined {
  if (!planName || !tpa) return undefined
  const tpaLabels = OP_LABELS[planName]?.[tpa]
  if (!tpaLabels) return undefined
  return tpaLabels[networkLevel]
}

/**
 * Get the IP network label for a Micro SME/SME plan based on plan name and TPA.
 * Returns undefined if the combination is not found.
 */
export function getIpNetworkLabel(
  planName: string | undefined,
  tpa: string | undefined,
): string | undefined {
  if (!planName || !tpa) return undefined
  return IP_LABELS[planName]?.[tpa]
}

